import React from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { MapPin, Settings, BarChart3 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Footer from './Footer';

interface LotAdmin {
  lot_id: string;
  role: string;
  lot: {
    id: string;
    slug: string;
    display_name: string;
    status: string;
  }[];
}

function LotSelection() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lots } = location.state as { lots: LotAdmin[] } || { lots: [] };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };

  if (!lots || lots.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Lots Available</h2>
          <p className="text-gray-600 mb-4">You don't have access to any parking lots.</p>
          <Link
            to="/admin"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'checker': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-6 space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex shadow-md rounded overflow-hidden h-6">
              <div className="w-4 h-full bg-green-600"></div>
              <div className="w-4 h-full bg-white"></div>
              <div className="w-4 h-full bg-red-600"></div>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Parking Little Italy</h1>
          </div>
          <p className="text-gray-600">Select a parking lot to manage</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {lots.map((lotAdmin) => {
            const lot = lotAdmin.lot[0]; // Access first element of lot array
            return (
              <div
                key={lot.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="text-blue-600" size={24} />
                    <div>
                      <h3 className="font-semibold text-gray-900">{lot.display_name}</h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(lotAdmin.role)}`}>
                        {lotAdmin.role.charAt(0).toUpperCase() + lotAdmin.role.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Link
                    to={`/admin/lots/${lot.slug}/dashboard`}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <BarChart3 size={16} />
                    Dashboard
                  </Link>
                  <Link
                    to={`/admin/lots/${lot.slug}/settings`}
                    className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Settings size={16} />
                    Settings
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            Sign out
          </button>
        </div>

        <Footer />
      </div>
    </div>
  );
}

export default LotSelection; 